import { ReactNode, useState } from 'react'
import {
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Modal
} from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { primary } from 'theme/globalStyles'
import { ITheme } from 'interfaces/theme'
import {
  DARK_PAGE_BG,
  DARK_TABLE_BORDER,
  DARK_TEXT_PRIMARY,
  DARK_TEXT_MUTED,
  DARK_GREY
} from 'constants/dark-tokens'

interface IConfirmModalProps {
  titleText?: string
  bodyText?: string | ReactNode
  cancelButtonText?: string
  confirmButtonText?: string
  confirmButtonColor?: string
  isOpen: boolean
  onClose: () => void
  onClickAccept: () => void
  onClickCancel?: () => void
  dark?: boolean
}

const ConfirmModal = (props: IConfirmModalProps) => {
  const {
    titleText = 'Confirm Changes',
    bodyText = 'Are you sure you want to do this?',
    cancelButtonText = 'Cancel',
    confirmButtonText = 'Confirm',
    confirmButtonColor,
    isOpen,
    onClose,
    onClickAccept,
    onClickCancel,
    dark = false
  } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { organizationStore } = useStores()
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}

  async function handleConfirm(): Promise<void> {
    setIsLoading(true)
    await onClickAccept()
    setIsLoading(false)
  }

  async function handleCancel(): Promise<void> {
    setIsLoading(true)
    if (onClickCancel) {
      await onClickCancel()
    }
    onClose()
    setIsLoading(false)
  }

  if (dark) {
    return (
      <Modal isCentered isOpen={isOpen} onClose={onClose}>
        <ModalOverlay bg="rgba(0,0,0,0.6)" />
        <ModalContent
          bg={DARK_PAGE_BG}
          border={`1px solid ${DARK_TABLE_BORDER}`}
          color={DARK_TEXT_PRIMARY}
          borderRadius="12px"
        >
          <ModalHeader
            fontSize="lg"
            lineHeight={7}
            fontWeight={700}
            color={DARK_TEXT_PRIMARY}
            borderBottom={`1px solid ${DARK_TABLE_BORDER}`}
          >
            {titleText}
          </ModalHeader>
          <ModalCloseButton
            color={DARK_TEXT_MUTED}
            border="none"
            boxShadow="none"
            _hover={{ color: DARK_TEXT_PRIMARY, bg: 'rgba(255,255,255,0.08)' }}
          />
          <ModalBody>
            <Text color={DARK_TEXT_MUTED} fontSize="md">
              {bodyText}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              bg="transparent"
              border={`1px solid ${DARK_GREY}`}
              color={DARK_TEXT_MUTED}
              borderRadius="6px"
              fontWeight={500}
              lineHeight={6}
              _hover={{ bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
              marginRight={4}
              paddingY={2}
              onClick={handleCancel}
              isLoading={isLoading}
            >
              {cancelButtonText}
            </Button>
            <Button
              borderRadius="6px"
              fontWeight={500}
              lineHeight={6}
              color="white"
              paddingY={2}
              border="none"
              onClick={handleConfirm}
              backgroundColor={confirmButtonColor ?? '#E53E3E'}
              _hover={{ bg: '#C53030' }}
              _focus={{ boxShadow: 'none' }}
              isLoading={isLoading}
            >
              {confirmButtonText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg" lineHeight={7} color="gray.800" fontWeight={700}>
          {titleText}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="gray.700" fontSize="md">
            {bodyText}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            color="gray.700"
            fontWeight={500}
            lineHeight={6}
            border="1px solid #E2E8F0"
            borderRadius="6px"
            background="transparent"
            _hover={{ background: 'gray.300' }}
            _active={{ background: 'gray.400' }}
            marginRight={4}
            paddingY={2}
            onClick={handleCancel}
            isLoading={isLoading}
          >
            {cancelButtonText}
          </Button>
          <Button
            borderRadius="6px"
            fontWeight={500}
            lineHeight={6}
            color="white"
            paddingY={2}
            border="none"
            onClick={handleConfirm}
            backgroundColor={confirmButtonColor ?? currentTheme?.primaryColor ?? primary}
            _hover={{ opacity: 0.8 }}
            isLoading={isLoading}
          >
            {confirmButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ConfirmModal
